import { IApiAxiosError } from "@/api";
import {
    checkSkillName,
    createSkill,
    getCoreSkillCount,
    publishSkill,
    ISkillCreate,
} from "@/api/skills";
import { CoreSkillToggle } from "@/components/shared/CoreSkillToggle";
import {
    MarkdownEditor,
    type MarkdownEditorHandle,
} from "@/components/shared/MarkdownEditor";
import { Modal, IModalAlert } from "@/components/shared/Modal";
import { OrganizationCombobox } from "@/components/shared/OrganizationCombobox";
import {
    collectReferenceMentionOccurrences,
    getBrokenReferenceTargets,
} from "@/utils/reference-mentions";
import {
    Box,
    Button,
    Chip,
    FormHelperText,
    FormLabel,
    IconButton,
    OutlinedInput,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DialogProps } from "@toolpad/core/useDialogs";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router";
import { type LocalFile, validateFileNames } from "../../skill-helpers";

// ── Component ────────────────────────────────────────────────────────

export const NewSkillDialog = ({ open, onClose }: DialogProps) => {
    const [name, setName] = React.useState("");
    const [organizationId, setOrganizationId] = React.useState<number | null>(
        null
    );
    const [isCore, setIsCore] = React.useState(false);
    const [description, setDescription] = React.useState("");
    const [content, setContent] = React.useState("");
    const [files, setFiles] = React.useState<LocalFile[]>([]);
    const [nameError, setNameError] = React.useState<string | null>(null);
    const [descError, setDescError] = React.useState<string | null>(null);
    const [contentError, setContentError] = React.useState<string | null>(null);
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const editorRef = useRef<MarkdownEditorHandle>(null);

    const { data: coreStats, error: coreCountError } = useQuery({
        queryKey: ["admin-skills-core-count", organizationId],
        queryFn: () => getCoreSkillCount(organizationId),
        enabled: open,
        retry: false,
    });

    const nameMaxLength = coreStats?.nameMaxLength;

    // ── Debounced name uniqueness check ──────────────────────────────
    useEffect(() => {
        const trimmed = name.trim();
        if (!trimmed) return;

        const timer = setTimeout(async () => {
            try {
                const { available } = await checkSkillName(trimmed);
                if (!available) {
                    setNameError("A skill with this name already exists");
                }
            } catch {
                // Silently ignore — submit-time validation will catch it
            }
        }, 400);

        return () => clearTimeout(timer);
    }, [name]);

    const fileNames = useMemo(() => {
        const names: string[] = [];
        const seen = new Set<string>();
        for (const file of files) {
            const fileName = file.name.trim();
            if (
                fileName.length === 0 ||
                fileName.length > 255 ||
                seen.has(fileName)
            ) {
                continue;
            }
            seen.add(fileName);
            names.push(fileName);
        }
        return names;
    }, [files]);

    // ── Broken @-refs ────────────────────────────────────────────────
    const brokenRefs = useMemo(() => {
        const targets = files.flatMap((file, index) => {
            const fileName = file.name.trim();
            return fileName.length > 0 && fileName.length <= 255
                ? [{ identity: `file:${index}`, name: fileName }]
                : [];
        });
        const sources = [
            { identity: "main", text: content },
            ...files.flatMap((file, index) =>
                file.name.trim().length > 0
                    ? [{ identity: `file:${index}`, text: file.content }]
                    : []
            ),
        ];
        return getBrokenReferenceTargets(
            collectReferenceMentionOccurrences(
                sources,
                fileNames,
                targets,
                "exact-case"
            )
        );
    }, [content, fileNames, files]);

    const hasFileErrors = useMemo(
        () => files.some((f) => f.nameError || f.contentError),
        [files]
    );

    const alerts = useMemo((): IModalAlert[] => {
        if (brokenRefs.length === 0) return [];
        const missing = [
            ...new Set(brokenRefs.map(({ typedTarget }) => typedTarget)),
        ];
        return [
            {
                severity: "warning",
                message: `Your instructions mention ${missing.map((t) => `"${t}"`).join(", ")} but ${missing.length === 1 ? "that file doesn't" : "those files don't"} exist yet. Add them below or remove the reference.`,
            },
        ];
    }, [brokenRefs]);

    // ── Validation ───────────────────────────────────────────────────
    const validate = useCallback((): boolean => {
        setDescError(null);
        setContentError(null);
        let ok = true;
        if (!name.trim()) {
            setNameError("Give your skill a name");
            ok = false;
        } else if (nameError) {
            // Already has a uniqueness error from debounce
            ok = false;
        }
        if (!description.trim()) {
            setDescError("Describe when this skill should be used");
            ok = false;
        } else if (description.length > 1024) {
            setDescError("Must be 1,024 characters or fewer");
            ok = false;
        }
        if (!content.trim()) {
            setContentError("Add instructions for AI Chat to follow");
            ok = false;
        }
        return ok;
    }, [name, nameError, description, content]);

    // ── Create mutation ──────────────────────────────────────────────
    const [isPublishing, setIsPublishing] = React.useState(false);

    const createMutation = useMutation({
        mutationFn: async ({
            data,
            publish,
        }: {
            data: ISkillCreate;
            publish: boolean;
        }) => {
            const skill = await createSkill(data);
            if (publish) {
                setIsPublishing(true);
                return await publishSkill(skill.id);
            }
            return skill;
        },
        onSuccess: (newSkill) => {
            queryClient.invalidateQueries({ queryKey: ["admin-skills"] });
            queryClient.invalidateQueries({ queryKey: ["admin-skills-all"] });
            queryClient.invalidateQueries({
                queryKey: ["admin-skills-core-count"],
            });
            navigate(`/admin/skills/${newSkill.id}/details`);
            handleClose();
        },
        onSettled: () => setIsPublishing(false),
    });

    const handleCreate = (publish: boolean) => {
        if (!validate()) return;

        createMutation.mutate({
            data: {
                name: name.trim(),
                description: description.trim(),
                content,
                files: files
                    .filter((f) => f.name.trim())
                    .map((f) => ({ name: f.name.trim(), content: f.content })),
                organization_id: organizationId,
                is_core: isCore,
            },
            publish,
        });
    };

    const handleClose = () => {
        onClose();
        setName("");
        setOrganizationId(null);
        setIsCore(false);
        setDescription("");
        setContent("");
        setFiles([]);
        setNameError(null);
        setDescError(null);
        setContentError(null);
        createMutation.reset();
    };

    // ── File management ──────────────────────────────────────────────
    const addFile = useCallback(() => {
        setFiles((prev) => [...prev, { name: "", content: "" }]);
    }, []);

    const updateFile = useCallback((idx: number, patch: Partial<LocalFile>) => {
        setFiles((prev) => {
            const next = [...prev];
            next[idx] = { ...next[idx], ...patch };
            return validateFileNames(next);
        });
    }, []);

    const removeFile = useCallback((idx: number) => {
        setFiles((prev) => prev.filter((_, i) => i !== idx));
    }, []);

    const axiosError = createMutation.error as IApiAxiosError;

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title="Create a New Skill"
            subtitle="Skills are reusable instruction sets that guide AI Chat through specific workflows."
            maxWidth="md"
            isLoading={createMutation.isPending}
            alerts={alerts}
            actions={[
                {
                    label:
                        createMutation.isPending && !isPublishing
                            ? "Saving..."
                            : "Save Draft",
                    onClick: () => handleCreate(false),
                    variant: "outlined",
                    color: "primary",
                    disabled: createMutation.isPending || hasFileErrors,
                },
                {
                    label: isPublishing
                        ? "Publishing..."
                        : createMutation.isPending
                          ? "Saving..."
                          : "Save and Publish",
                    onClick: () => handleCreate(true),
                    variant: "contained",
                    color: "primary",
                    disabled:
                        createMutation.isPending ||
                        hasFileErrors ||
                        brokenRefs.length > 0,
                },
            ]}
            footerText={
                axiosError && (
                    <Typography
                        variant="caption"
                        color="error"
                        sx={{ fontSize: "0.75rem" }}
                    >
                        {axiosError.response?.data.detail ||
                            "Something went wrong. Please try again."}
                    </Typography>
                )
            }
        >
            <Stack spacing={2.5}>
                {/* ── Skill Name ── */}
                <Stack spacing={0.5}>
                    <FormLabel error={!!nameError}>Skill Name</FormLabel>
                    <OutlinedInput
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value);
                            setNameError(null);
                        }}
                        autoFocus
                        error={!!nameError}
                        placeholder="Broker Renewal Advisory"
                        inputProps={{ maxLength: nameMaxLength }}
                        size="small"
                        fullWidth
                    />
                    {(nameError || nameMaxLength != null) && (
                        <FormHelperText error={!!nameError}>
                            {nameError || `${name.length}/${nameMaxLength}`}
                        </FormHelperText>
                    )}
                </Stack>

                {/* ── Organization ── */}
                <Stack spacing={0.5}>
                    <FormLabel>Organization</FormLabel>
                    <OrganizationCombobox
                        value={organizationId}
                        onChange={setOrganizationId}
                    />
                    <FormHelperText>
                        Leave blank for global visibility across all
                        organizations
                    </FormHelperText>
                </Stack>

                {/* ── Core Skill ── */}
                <CoreSkillToggle
                    isCore={isCore}
                    onChange={setIsCore}
                    coreCount={coreStats?.coreCount}
                    coreCountScope={organizationId == null ? "global" : "org"}
                    error={
                        coreCountError
                            ? "Core skill count unavailable"
                            : undefined
                    }
                    includesThisSkill={false}
                />

                {/* ── When to Use ── */}
                <Stack spacing={0.5}>
                    <FormLabel error={!!descError}>When to Use</FormLabel>
                    <OutlinedInput
                        value={description}
                        onChange={(e) => {
                            setDescription(e.target.value);
                            setDescError(null);
                        }}
                        multiline
                        minRows={2}
                        error={!!descError}
                        placeholder="Describe when this skill should activate, e.g. helping brokers prepare renewal reports"
                        inputProps={{ maxLength: 1024 }}
                        size="small"
                        fullWidth
                    />
                    <FormHelperText error={!!descError}>
                        {descError || `${description.length}/1024`}
                    </FormHelperText>
                </Stack>

                {/* ── Instructions ── */}
                <Stack spacing={0.5}>
                    <FormLabel error={!!contentError}>
                        Instructions{" "}
                        <Typography
                            component="span"
                            variant="caption"
                            color="text.disabled"
                        >
                            (Markdown)
                        </Typography>
                    </FormLabel>
                    <MarkdownEditor
                        ref={editorRef}
                        value={content}
                        onChange={(md) => {
                            setContent(md);
                            setContentError(null);
                        }}
                        placeholder="The step-by-step guide AI Chat follows when this skill activates..."
                        minHeight={240}
                        error={!!contentError}
                        suggestions={fileNames}
                        referenceResolution="exact-case"
                    />
                    {fileNames.length > 0 && (
                        <Stack
                            direction="row"
                            spacing={0.5}
                            flexWrap="wrap"
                            useFlexGap
                        >
                            {fileNames.map((fname) => (
                                <Chip
                                    key={fname}
                                    label={`@${fname}`}
                                    size="small"
                                    onClick={() =>
                                        editorRef.current?.insertText(
                                            `@${fname} `
                                        )
                                    }
                                    sx={{
                                        cursor: "pointer",
                                        fontSize: "0.75rem",
                                        bgcolor: "grey.200",
                                        color: "text.primary",
                                    }}
                                />
                            ))}
                        </Stack>
                    )}
                    {contentError && (
                        <FormHelperText error>{contentError}</FormHelperText>
                    )}
                </Stack>

                {/* ── Supporting Files ── */}
                <Stack spacing={1}>
                    <Stack direction="row" alignItems="center">
                        <Stack spacing={0.25}>
                            <FormLabel sx={{ color: "text.secondary" }}>
                                Supporting Files
                            </FormLabel>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Add reference docs, templates, or examples.
                                Mention them in your instructions with @name.
                            </Typography>
                        </Stack>
                        <Box flexGrow={1} />
                        <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={addFile}
                            sx={{ textTransform: "none", flexShrink: 0 }}
                        >
                            Add File
                        </Button>
                    </Stack>

                    {files.map((file, idx) => (
                        <Stack
                            key={`file-${idx}`}
                            spacing={1}
                            sx={{
                                p: 2,
                                bgcolor: "grey.50",
                                border: 1,
                                borderColor:
                                    file.nameError || file.contentError
                                        ? "error.main"
                                        : "divider",
                                borderRadius: "8px",
                            }}
                        >
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                            >
                                <Stack spacing={0.5} sx={{ flex: 1 }}>
                                    <FormLabel error={!!file.nameError}>
                                        Reference name
                                    </FormLabel>
                                    <OutlinedInput
                                        value={file.name}
                                        onChange={(e) =>
                                            updateFile(idx, {
                                                name: e.target.value,
                                            })
                                        }
                                        size="small"
                                        fullWidth
                                        error={!!file.nameError}
                                        placeholder="Renewal Template"
                                        inputProps={{ maxLength: 255 }}
                                    />
                                    {file.nameError && (
                                        <FormHelperText error>
                                            {file.nameError}
                                        </FormHelperText>
                                    )}
                                </Stack>
                                <Tooltip title="Remove file">
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => removeFile(idx)}
                                        sx={{ flexShrink: 0 }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                            <MarkdownEditor
                                value={file.content}
                                onChange={(md) =>
                                    updateFile(idx, { content: md })
                                }
                                placeholder="Reference material, examples, or templates for this skill to use..."
                                minHeight={120}
                                error={!!file.contentError}
                                suggestions={fileNames}
                                referenceResolution="exact-case"
                            />
                            {file.contentError && (
                                <FormHelperText error>
                                    {file.contentError}
                                </FormHelperText>
                            )}
                        </Stack>
                    ))}
                </Stack>
            </Stack>
        </Modal>
    );
};
